import React from 'react';
import { generatePath, Outlet } from 'react-router';

import { PERMISSIONS } from 'api/auth/constants';
import { ICONS_MAP } from 'constants/icons';
import { PERMISSION_TYPE } from 'hooks/useRBAC';
import { ROUTES } from 'routes/config/constants';
import { IRoutesConfigItem } from 'routes/config/index';
import { appLazy } from 'routes/ErrorBoundary/appReloadLazy';

const Settings = appLazy(
  () => import('routes/pages/AuthorizedStack/Settings/PortalSettings'),
);

const Notification = appLazy(
  () => import('routes/pages/AuthorizedStack/Settings/Notification'),
);

const UsersAuditLogs = appLazy(
  () => import('routes/pages/AuthorizedStack/Settings/UsersAuditLogs'),
);

const AuthAuditLogs = appLazy(
  () => import('routes/pages/AuthorizedStack/Settings/AuthAuditLogs'),
);

const CreateNotification = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/Settings/Notification/Notifications/Notification/Create'
    ),
);

const EditNotification = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/Settings/Notification/Notifications/Notification/Edit'
    ),
);

const CopyTemplate = appLazy(
  () =>
    import('routes/pages/AuthorizedStack/Settings/Notification/Templates/Template/Copy'),
);

const CreateTemplate = appLazy(
  () =>
    import(
      'routes/pages/AuthorizedStack/Settings/Notification/Templates/Template/Create'
    ),
);

const EditTemplate = appLazy(
  () =>
    import('routes/pages/AuthorizedStack/Settings/Notification/Templates/Template/Edit'),
);

export const SETTINGS_CHILDREN: IRoutesConfigItem[] = [
  {
    breadcrumb: 'settings.title',
    component: Settings,
    isSubFolder: true,
    navBarIcon: <ICONS_MAP.WlStylization />,
    path: ROUTES.SETTINGS.SUB_PATH.PORTAL_SETTINGS.PATH,
    permissions: [PERMISSIONS.CAN_CHANGE_MP_STYLIZATION],
    testId: 'navbarStylization',
    url: ROUTES.SETTINGS.SUB_PATH.PORTAL_SETTINGS.PATH,
  },
  {
    breadcrumb: 'notification.title',
    children: [
      {
        component: Notification,
        path: ROUTES.SETTINGS.SUB_PATH.NOTIFICATIONS.PATH,
        permissions: [
          PERMISSIONS.CAN_VIEW_OUTPUT_CHANNELS,
          PERMISSIONS.CAN_VIEW_NOTIFICATIONS,
          PERMISSIONS.CAN_VIEW_TEMPLATES,
        ],
        permissionType: PERMISSION_TYPE.OR,
      },
      {
        component: CreateNotification,
        path: ROUTES.SETTINGS.SUB_PATH.NOTIFICATIONS.SUB_PATH.CREATE_NOTIFICATION,
        permissions: [PERMISSIONS.CAN_CREATE_NOTIFICATION],
      },
      {
        component: EditNotification,
        path: ROUTES.SETTINGS.SUB_PATH.NOTIFICATIONS.SUB_PATH.EDIT_NOTIFICATION.PATH,
        permissions: [PERMISSIONS.CAN_UPDATE_NOTIFICATION],
        url: (params) => {
          const id = params?.id;
          const { PARAMS, PATH } =
            ROUTES.SETTINGS.SUB_PATH.NOTIFICATIONS.SUB_PATH.EDIT_NOTIFICATION;
          return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
        },
      },
      {
        component: CreateTemplate,
        path: ROUTES.SETTINGS.SUB_PATH.NOTIFICATIONS.SUB_PATH.CREATE_TEMPLATES,
        permissions: [PERMISSIONS.CAN_CREATE_TEMPLATE],
      },
      {
        component: CopyTemplate,
        path: ROUTES.SETTINGS.SUB_PATH.NOTIFICATIONS.SUB_PATH.COPY_TEMPLATES.PATH,
        permissions: [PERMISSIONS.CAN_CREATE_TEMPLATE],
        url: (params) => {
          const id = params?.id;
          const { PARAMS, PATH } =
            ROUTES.SETTINGS.SUB_PATH.NOTIFICATIONS.SUB_PATH.COPY_TEMPLATES;
          return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
        },
      },
      {
        component: EditTemplate,
        path: ROUTES.SETTINGS.SUB_PATH.NOTIFICATIONS.SUB_PATH.EDIT_TEMPLATES.PATH,
        permissions: [PERMISSIONS.CAN_UPDATE_TEMPLATE],
        url: (params) => {
          const id = params?.id;
          const { PARAMS, PATH } =
            ROUTES.SETTINGS.SUB_PATH.NOTIFICATIONS.SUB_PATH.EDIT_TEMPLATES;
          return !id ? '' : generatePath(PATH, { [PARAMS.ID]: id });
        },
      },
    ],
    component: Outlet,
    isSubFolder: true,
    navBarIcon: <ICONS_MAP.Notifications />,
    path: ROUTES.SETTINGS.SUB_PATH.NOTIFICATIONS.PATH,
    permissions: [
      PERMISSIONS.CAN_VIEW_OUTPUT_CHANNELS,
      PERMISSIONS.CAN_VIEW_NOTIFICATIONS,
      PERMISSIONS.CAN_VIEW_TEMPLATES,
    ],
    permissionType: PERMISSION_TYPE.OR,
    testId: 'navbarNotification',
    url: ROUTES.SETTINGS.SUB_PATH.NOTIFICATIONS.PATH,
  },
  {
    breadcrumb: 'usersAuditLogs.title',
    component: UsersAuditLogs,
    isSubFolder: true,
    navBarIcon: <ICONS_MAP.AuditLog />,
    path: ROUTES.SETTINGS.SUB_PATH.USERS_AUDIT_LOGS.PATH,
    permissions: [PERMISSIONS.CAN_VIEW_USERS_AUDIT_LOG],
    testId: 'navbarUsersAuditLogs',
    url: ROUTES.SETTINGS.SUB_PATH.USERS_AUDIT_LOGS.PATH,
  },
  {
    breadcrumb: 'authAuditLogs.title',
    component: AuthAuditLogs,
    isSubFolder: true,
    navBarIcon: <ICONS_MAP.AuthAuditLog />,
    path: ROUTES.SETTINGS.SUB_PATH.AUTH_AUDIT_LOGS.PATH,
    permissions: [PERMISSIONS.VIEW_AUTH_AUDIT_LOGS],
    testId: 'navbarAuthAuditLogs',
    url: ROUTES.SETTINGS.SUB_PATH.AUTH_AUDIT_LOGS.PATH,
  },
];
